import type { NavItem } from './components/SiteChrome';

/** Os modulos que o seletor do cabecalho liga entre si, mais o back-office. */
export type ModuleId = 'site' | 'arena' | 'streaming' | 'gestao';

export type ModuleOrigins = Record<ModuleId, string>;

type Where = Pick<Location, 'protocol' | 'hostname' | 'port'>;

/** Portas locais: o compose (8080-8083) e o `vite` de cada app (5173-5176). */
const LOCAL_PORTS: Record<'compose' | 'vite', Record<ModuleId, string>> = {
  compose: { arena: '8080', site: '8081', streaming: '8082', gestao: '8083' },
  vite: { arena: '5173', site: '5174', streaming: '5175', gestao: '5176' },
};

const PREFIXES = ['arena.', 'streaming.', 'gestao.', 'site.', 'www.'];

/**
 * Onde esta cada modulo, deduzido do endereco onde a pagina esta aberta: a
 * mesma imagem serve staging e producao sem configuracao.
 *
 *   growup.gg / arena.growup.gg      -> growup.gg, arena., streaming., gestao.
 *   *.staging.growup.gg              -> site.staging..., arena.staging..., ...
 *   localhost:8080-8083 ou 5173-5176 -> as outras portas do mesmo grupo
 */
export function moduleOrigins(where: Where = window.location): ModuleOrigins {
  const { protocol, hostname, port } = where;
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    const ports = Object.values(LOCAL_PORTS.vite).includes(port)
      ? LOCAL_PORTS.vite
      : LOCAL_PORTS.compose;
    const at = (id: ModuleId) => `${protocol}//${hostname}:${ports[id]}`;
    return {
      site: at('site'),
      arena: at('arena'),
      streaming: at('streaming'),
      gestao: at('gestao'),
    };
  }
  const prefix = PREFIXES.find((p) => hostname.startsWith(p));
  const base = prefix ? hostname.slice(prefix.length) : hostname;
  // Em producao o site e o dominio sem prefixo; em staging tem `site.`.
  const siteHost = base.split('.').length > 2 ? `site.${base}` : base;
  const at = (host: string) => `${protocol}//${host}${port ? `:${port}` : ''}`;
  return {
    site: at(siteHost),
    arena: at(`arena.${base}`),
    streaming: at(`streaming.${base}`),
    gestao: at(`gestao.${base}`),
  };
}

/**
 * Os itens do seletor «Site · Arena · Streamers». A Arena publica e a pagina
 * /arena do site: arena.* serve os PCs da sala e reencaminha para la. Os
 * Streamers publicos sao a pagina /streamers do site; o Stream Portal
 * (streaming.*) fica para a equipa de streamers.
 *
 * `gestao`: a conta tem permissoes no back-office (as `grants` do token); so
 * entao aparece «Gestão», que abre gestao.* (com login proprio e 2FA).
 */
export function moduleNav(
  current: 'site' | 'arena' | 'streaming',
  origins: ModuleOrigins = moduleOrigins(),
  { gestao = false }: { gestao?: boolean } = {},
): NavItem[] {
  return [
    { label: 'Site', href: `${origins.site}/`, current: current === 'site' },
    { label: 'Arena', href: `${origins.site}/arena`, current: current === 'arena' },
    { label: 'Streamers', href: `${origins.site}/streamers`, current: current === 'streaming' },
    ...(gestao ? [{ label: 'Gestão', href: `${origins.gestao}/`, current: false }] : []),
  ];
}
